import client, {
    adminClient,
    authAdminClient,
    authClient,
    explorerClient,
} from 'helper/httpClient';
import {get, isNil} from 'lodash';
import {useAuthStore} from 'stores/authStore';
import {cleanObjectNilValue} from 'utils/clean';
import {useLocale} from './useLocale';
import {some} from 'lodash';
import {useUIStore} from 'stores/uiStore';
import {saleClient} from 'helper/httpClient';
import appConfig from 'config/app';


export const isUserRole = (roles) => {
    return (
        roles.includes('USER') ||
        roles.includes('CLIENT_USER') ||
        roles.includes('CLIENT_COMPANY_ADMIN') ||
        roles.includes('CLIENT_COMPANY_OPERATOR') ||
        roles.includes('CLIENT_COMPANY_READONLY') ||
        roles.includes('CLIENT_COMPANY_CONTACT')
    );
};


export const isAdminRole = (roles) => {
    return (
        roles.includes('ADMIN') ||
        roles.includes('RISK_COMPLIANCE') ||
        roles.includes('IT_ADMIN') ||
        roles.includes('MARKETING') ||
        roles.includes('SUPPORT') ||
        roles.includes('PLATFORM_MARKETING') ||
        roles.includes('SUPPORT_MANAGER')||
        roles.includes('FINANCE') ||
        roles.includes('IB_ADMIN') ||
        roles.includes('SALE_MANAGER') ||
        roles.includes('READONLY_ADMIN') ||
        roles.includes('SALE')
    );
};


export const useAuth = () => {
    const state = useAuthStore((state) => state);
    const {locale} = useLocale();
    const isAdmin = get(state, 'user.roles', []).includes('ADMIN');
    const isReadOnlyAdmin = get(state, 'user.roles', []).includes('READONLY_ADMIN');
    const isSupport = get(state, 'user.roles', []).includes('SUPPORT');
    const isSupportManager = get(state, 'user.roles', []).includes(
        'SUPPORT_MANAGER',
    );
    const isSale = get(state, 'user.roles', []).includes('SALE');
    const isSaleManager = get(state, 'user.roles', []).includes('SALE_MANAGER');
    const isUser = get(state, 'user.roles', []).includes('USER');
    const isRiskCompliance = get(state, 'user.roles', []).includes(
        'RISK_COMPLIANCE',
    );
    const isFinance = get(state, 'user.roles', []).includes('FINANCE');
    const isITAdmin = get(state, 'user.roles', []).includes('IT_ADMIN');
    const isMarketing = get(state, 'user.roles', []).includes('MARKETING');
    const [setFirstLanguageCounter] = useUIStore((state) => [
        state.setFirstLanguageCounter,
    ]);
    return {
        ...state,
        id: get(state, 'user.id') || null,
        roles: get(state, 'user.roles') || [],
        isAdmin,
        isReadOnlyAdmin,
        isSupport,
        isSupportManager,
        isSale,
        isSaleManager,
        isUser,
        isRiskCompliance,
        isFinance,
        isITAdmin,
        isMarketing,
        isAdminPage: isAdmin || isSupport || isSale,
        // isUser: some(get(state, 'user.roles', []),o=>o.role==='USER'),
        isLoggedIn: Boolean(get(state, 'token')),
        login: async ({username, password, issued, code, method, recordId}) => {
            try {

                let requestClient = authClient;
                if (process.env.REACT_APP_PERMISSION_CONTROL) {
                    const permissions = process.env.REACT_APP_PERMISSION_CONTROL.split(',');
                    if (isAdminRole(permissions)){
                        requestClient = authAdminClient;
                    }
                }

                const {result: auth} = await requestClient.post(
                    ['login'],
                    cleanObjectNilValue({
                        username,
                        password,
                        issued,
                        code,
                        method,
                        recordId,
                    }),
                );

                if (auth?.sendCodeKey) {
                    return auth;
                }

                if (!some(get(auth, 'user.roles', []), (rule) =>
                        appConfig.appPermissionControl.includes(rule),
                    )
                ) {
                    throw new Error('permissionDenied');
                }

                const isUser = isUserRole(get(auth, 'user.roles'));
                if (isUser) {
                    client.onSending = (req) => {
                        req.set(
                            'Authorization',
                            `bearer ${get(auth, 'token')}`,
                        );
                    };
                    explorerClient.onSending = (req) => {
                        req.set(
                            'Authorization',
                            `bearer ${get(auth, 'token')}`,
                        );
                    };
                }

                const isAdmin = isAdminRole(get(auth, 'user.roles'));
                if (isAdmin){
                    adminClient.onSending = (req) => {
                        req.set(
                            'Authorization',
                            `Bearer ${get(auth, 'token')}`,
                        );
                    };
                }

                state.setToken(get(auth, 'token'));
                state.setUser(get(auth, 'user'));
                return auth;
            } catch (error) {
                throw error;
            }
        },
        logout: (noRedirect) => {
            state.logout();
            delete client.onSending;
            delete adminClient.onSending;
            setFirstLanguageCounter(1);
        },
        register: async ({
            email,
            phoneCode,
            phoneNo,
            phone,
            type,
            password,
            inviterCode,
            firstName,
            lastName,
            companyName,
            region,
            secretKey,
            recordId,
            heardFrom,
            source
        }) => {
            const {result} = await authClient.post(
                ['register'],
                cleanObjectNilValue({
                    email,
                    phoneCode,
                    phoneNo,
                    phone,
                    type,
                    password,
                    inviterCode,
                    locale,
                    firstname: firstName,
                    lastname: lastName,
                    companyName,
                    region,
                    secretKey,
                    recordId,
                    heardFrom,
                    source
                }),
            );
            return result;
        },
        init: () => {
            if (state.token) {
                client.onSending = (req) => {
                    req.set('Authorization', `bearer ${state.token}`);
                };
            }
        },
        authVerifyEmail: async ({issueId, code}) => {
            let requestClient = authClient;
            if (process.env.REACT_APP_PERMISSION_CONTROL) {
                const permissions = process.env.REACT_APP_PERMISSION_CONTROL.split(',');
                if (isAdminRole(permissions)){
                    requestClient = authAdminClient;
                }
            }

            const {result} = await requestClient.post(
                ['auth-verify-email'],
                cleanObjectNilValue({
                    issueId,
                    code,
                }),
            );
            return result;
        },
        sendCode: async ({method, sendCodeKey, issueId}) => {
            let requestClient = authClient;
            if (process.env.REACT_APP_PERMISSION_CONTROL) {
                const permissions = process.env.REACT_APP_PERMISSION_CONTROL.split(',');
                if (isAdminRole(permissions)){
                    requestClient = authAdminClient;
                }
            }

            const {result} = await requestClient.post(
                ['send-code'],
                cleanObjectNilValue({
                    method,
                    sendCodeKey,
                    issueId,
                }),
            );
            return result;
        },
        verify: async ({method, issueId, code}) => {
            let requestClient = authClient;
            if (process.env.REACT_APP_PERMISSION_CONTROL) {
                const permissions = process.env.REACT_APP_PERMISSION_CONTROL.split(',');
                if (isAdminRole(permissions)){
                    requestClient = authAdminClient;
                }
            }

            const {result: auth} = await requestClient.post(
                ['verify'],
                cleanObjectNilValue({
                    issueId,
                    code,
                    method,
                }),
            );
            const isUser = isUserRole(get(auth, 'user.roles'));
            if (
                !some(get(auth, 'user.roles', []), (rule) =>
                    appConfig.appPermissionControl.includes(rule),
                )
            ) {
                throw new Error('permissionDenied');
            }
            if (isUser) {
                client.onSending = (req) => {
                    req.set('Authorization', `bearer ${get(auth, 'token')}`);
                };
                explorerClient.onSending = (req) => {
                    req.set('Authorization', `bearer ${get(auth, 'token')}`);
                };
            }

            const isAdmin = isAdminRole(get(auth, 'user.roles'));
            if (isAdmin){
                adminClient.onSending = (req) => {
                    req.set(
                        'Authorization',
                        `Bearer ${get(auth, 'token')}`,
                    );
                };
            }

            state.setToken(get(auth, 'token'));
            state.setUser(get(auth, 'user'));
            return auth;
        },
    };
};
